import React, { useEffect, useState } from 'react';
import { Clock, Award, ArrowRight, CheckCircle2, GraduationCap } from 'lucide-react';
import { COURSES } from '../mock/mock';
import { useToast } from '../hooks/use-toast';

const LEVEL_COLOR = { Entry: 'bg-[#15a34a]', Intermediate: 'bg-[#f5a623]', Advanced: 'bg-[#c8102e]' };

const Training = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { toast } = useToast();
  const [filter, setFilter] = useState('All');
  const levels = ['All', 'Entry', 'Intermediate', 'Advanced'];
  const list = filter === 'All' ? COURSES : COURSES.filter((c) => c.level === filter);

  const book = (title) => toast({ title: 'Enquiry received (demo)', description: `We\u2019ll be in touch about ${title}.` });

  return (
    <div>
      <div className="relative bg-[#141414] text-white py-20 overflow-hidden">
        <img src="https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141414] to-[#141414]/60" />
        <div className="container-fes relative">
          <span className="section-label">Training Academy</span>
          <h1 className="font-head text-4xl md:text-5xl uppercase mt-2 max-w-2xl">Accredited Life-Saving Training</h1>
          <p className="text-gray-300 mt-4 max-w-2xl text-lg">Nationally recognised courses delivered by working medics — hands-on, realistic and built for real-world response.</p>
        </div>
      </div>

      <section className="py-16 md:py-20 bg-white">
        <div className="container-fes">
          <div className="flex flex-wrap gap-3 mb-10 justify-center">
            {levels.map((l) => (
              <button key={l} onClick={() => setFilter(l)} className={`font-cond uppercase tracking-wide px-5 py-2 rounded-full transition-colors ${filter === l ? 'bg-[#15a34a] text-white' : 'border border-gray-200 text-[#141414] hover:border-[#c8102e]'}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((c) => (
              <div key={c.id} className="group bg-white rounded-lg overflow-hidden border border-gray-100 hover-lift flex flex-col">
                <div className="relative aspect-video overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded font-cond text-white ${LEVEL_COLOR[c.level]}`}>{c.level}</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs font-cond uppercase tracking-wide text-gray-400">{c.code}</span>
                  <h3 className="font-head text-xl uppercase text-[#141414] mt-1 mb-2">{c.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-1">{c.desc}</p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5"><Clock size={15} className="text-[#c8102e]" /> {c.duration}</span>
                    <span className="flex items-center gap-1.5"><Award size={15} className="text-[#c8102e]" /> Certified</span>
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-50">
                    <span className="font-head text-2xl text-[#141414]">${c.price}</span>
                    <button onClick={() => book(c.title)} className="btn-crimson py-2 px-4 text-sm">Book Now</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f4f6fa]">
        <div className="container-fes grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="section-label">On-Site & Corporate</span>
            <h2 className="font-head text-3xl md:text-4xl uppercase text-[#141414] mt-2 mb-5">Bring Training to Your Team</h2>
            <p className="text-gray-600 mb-6 leading-relaxed">We deliver group and corporate training at your workplace or site. Tailored scenarios, real equipment and nationally recognised outcomes.</p>
            <ul className="space-y-3">
              {['Flexible on-site scheduling', 'Group discounts for 8+ participants', 'Custom scenario-based simulation', 'Certificates issued on completion'].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#141414]"><CheckCircle2 size={18} className="text-[#c8102e]" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-lg p-8 border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <GraduationCap size={28} className="text-[#c8102e]" />
              <h3 className="font-head text-2xl uppercase text-[#141414]">Enquire About Group Training</h3>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); toast({ title: 'Enquiry sent (demo)', description: 'Our training team will contact you shortly.' }); e.target.reset(); }} className="space-y-4">
              <input required placeholder="Your name" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#c8102e] transition-colors" />
              <input required type="email" placeholder="Email" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#c8102e] transition-colors" />
              <input placeholder="Number of participants" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#c8102e] transition-colors" />
              <textarea rows="3" placeholder="Which course are you interested in?" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#c8102e] transition-colors resize-none" />
              <button className="btn-crimson w-full">Send Enquiry <ArrowRight size={16} /></button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Training;
